/**
 * Utilidades para detectar nombres de propiedad duplicados o "parecidos".
 *
 * Se usa en el modal de crear/editar propiedad (PropiedadIdentityPanel) para:
 *  - BLOQUEAR nombres exactamente iguales (tratando mayúsculas/minúsculas y
 *    espacios redundantes como lo mismo) — la regla "no puede haber dos
 *    iguales".
 *  - ADVERTIR (sin bloquear) cuando hay nombres que se ven o suenan parecidos,
 *    para que el admin recuerde qué tan distintos tiene los nombres y no se
 *    confunda (p.ej. "LATORRE 614" vs "LATORRE 844").
 *
 * El bloqueo real vive además en el backend (createProperty/updateProperty);
 * esto es la capa de UX en vivo. Es barato: ~70 propiedades, Levenshtein sobre
 * strings cortos, recalculado al tipear (con el debounce natural de React).
 */

import { norm } from "./fuzzyMatch";

/**
 * Normaliza un nombre para comparar IGUALDAD exacta: recorta los extremos,
 * colapsa espacios internos y pasa a minúsculas. Mantiene los acentos a
 * propósito — dos nombres que solo difieren en un acento se consideran
 * "parecidos", no "iguales". Ej: "LATORRE  844 " y "latorre 844" → iguales.
 */
export function normalizeExact(s: string | null | undefined): string {
  return String(s ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/** Distancia de edición de Levenshtein (DP por filas, O(n·m) memoria O(m)). */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/**
 * Puntaje de similitud 0..1 entre dos nombres, pensado para detectar nombres
 * que "se ven o suenan parecidos". Compara sobre el texto SIN acentos
 * (accent-insensitive vía `norm`). Combina:
 *  - Ratio de Levenshtein (parecido visual / typos: "latorre 844" ↔
 *    "latorre 814").
 *  - Solapamiento de tokens (Jaccard) para nombres que comparten palabras
 *    pero difieren en largo ("Casa Azul" ↔ "Casa Azul Norte").
 * Devuelve el máximo de ambos.
 */
export function similarityScore(a: string, b: string): number {
  const na = norm(a);
  const nb = norm(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;

  const lev = levenshtein(na, nb);
  const levRatio = 1 - lev / Math.max(na.length, nb.length);

  const ta = new Set(na.split(/\s+/).filter(Boolean));
  const tb = new Set(nb.split(/\s+/).filter(Boolean));
  let inter = 0;
  ta.forEach((t) => {
    if (tb.has(t)) inter++;
  });
  const union = new Set([...ta, ...tb]).size;
  const jaccard = union ? inter / union : 0;

  return Math.max(levRatio, jaccard);
}

export interface NameItem {
  id: number;
  nombrepropiedad: string;
}

export interface NameMatchResult {
  /** Nombres idénticos (case/espacios-insensible), excluyendo `excludeId`. */
  exact: NameItem[];
  /** Nombres parecidos (no idénticos), ordenados por similitud descendente. */
  similar: NameItem[];
}

/**
 * Clasifica las propiedades existentes frente a `name` en idénticas vs
 * parecidas.
 *
 * @param name      nombre que el admin está escribiendo
 * @param list      propiedades existentes (id + nombre)
 * @param opts.excludeId  id de la propia propiedad en edición (se omite)
 * @param opts.threshold  umbral de similitud para "parecido" (0..1, def 0.6)
 * @param opts.limit      máximo de parecidos a devolver (def 6)
 */
export function findNameMatches(
  name: string,
  list: NameItem[],
  opts: { excludeId?: number | null; threshold?: number; limit?: number } = {},
): NameMatchResult {
  const { excludeId = null, threshold = 0.6, limit = 6 } = opts;
  const target = normalizeExact(name);
  const exact: NameItem[] = [];
  if (!target) return { exact, similar: [] };

  const scored: { item: NameItem; score: number }[] = [];
  for (const item of list) {
    if (excludeId != null && item.id === excludeId) continue;
    if (normalizeExact(item.nombrepropiedad) === target) {
      exact.push(item);
      continue;
    }
    const score = similarityScore(name, item.nombrepropiedad);
    if (score >= threshold) scored.push({ item, score });
  }

  scored.sort((a, b) => b.score - a.score);
  return { exact, similar: scored.slice(0, limit).map((s) => s.item) };
}
